import type { Device, ExpoToken, User, UserName } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { isNonNil, now, strongEntries, strongFromEntries } from '@shady-pines/types';
import { isEmpty, isNil } from 'lodash';
import { supabaseApi } from '../api/api-store';
import { CURRENT_APP_VERSION } from '../util';
import type { UserSearchResult, UserState } from './types';

type UsersResponse = {
  users: UserSearchResult[];
  nextCursor: string | null;
};

export type UserApi = ReturnType<typeof UserApi>;

export const UserApi = (
  get: () => UserState,
  set: (partial: Partial<UserState> | ((state: UserState) => Partial<UserState>)) => void,
) =>
  ({
    fetchMyProfile: async () => {
      const loadingMyProfile = get().loading.myProfile;
      console.log('profile load is already in progress?', loadingMyProfile);
      if (loadingMyProfile) {
        return;
      }

      const userId = get().me;
      console.log('whoami', userId);

      if (isNil(userId)) {
        console.log('no user id defined, resetting profile');
        set(
          (s) =>
            ({
              myProfile: null,
              isAdmin: false,
              loading: {
                ...s.loading,
                myProfile: false,
              },
            }) as const satisfies Partial<UserState>,
        );

        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              myProfile: true,
            },
          }) as const satisfies Partial<UserState>,
      );

      console.log('we have a user id! fetching profile');

      const { data: myProfile } = await supabaseApi()
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .is('deleted_at', null)
        .maybeSingle();

      console.log('fetched user profile', myProfile);

      set(
        (s) =>
          ({
            myProfile,
            isAdmin: myProfile?.role === 'admin',
            loading: {
              ...s.loading,
              myProfile: false,
            },
          }) as const satisfies Partial<UserState>,
      );
    },
    updateMyUserMetadata: async (deviceType: 'windows' | 'android' | 'ios' | 'macos' | 'web') => {
      const myProfile = get().myProfile;
      if (isNil(myProfile)) {
        return;
      }

      if (myProfile.app_version === CURRENT_APP_VERSION && myProfile.device_type === deviceType) {
        return;
      }

      const updatingApp = get().loading.myMetadata;
      if (updatingApp) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              myMetadata: true,
            },
          }) as const satisfies Partial<UserState>,
      );

      const { data: updatedProfile } = await supabaseApi()
        .from('profiles')
        .update({
          updated_at: now(),
          app_version: CURRENT_APP_VERSION,
          device_type: deviceType,
        })
        .eq('id', myProfile.id)
        .select()
        .single();

      set(
        (s) =>
          ({
            myProfile: updatedProfile,
            loading: {
              ...s.loading,
              myMetadata: false,
            },
          }) as const satisfies Partial<UserState>,
      );
    },
    updateUserName: async (newUserName: UserName) => {
      const myProfile = get().myProfile;
      if (isNil(myProfile) || myProfile.user_name === newUserName) {
        return;
      }

      const updating = get().loading.updateUserName;
      if (updating) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateUserName: true,
            },
          }) as const satisfies Partial<UserState>,
      );

      const { error: e, data: updatedProfile } = await supabaseApi()
        .from('profiles')
        .update({ user_name: newUserName, updated_at: now() })
        .eq('id', myProfile.id)
        .select('*')
        .single();

      set(
        (s) =>
          ({
            myProfile: updatedProfile ?? s.myProfile,
            loading: {
              ...s.loading,
              updateUserName: false,
            },
          }) as const satisfies Partial<UserState>,
      );

      return e;
    },
    fetchProfilesById: async (profileIds: Uuid<User>[]) => {
      const profilesAlreadyFetching = strongEntries(get().loading.fetchingProfilesById)
        .filter(([_, fetching]) => fetching)
        .map(([id]) => id);
      const profiles = get().profilesById;
      const profilesNotFetched = profileIds.filter(
        (id) => isNil(profiles[id]) && !profilesAlreadyFetching.includes(id),
      );

      if (isEmpty(profilesNotFetched)) {
        console.log('[fetchProfilesById] all already fetched, skipping');
        return;
      }
      console.log('[fetchProfilesById] fetching:', profilesNotFetched);

      set((s) => ({
        loading: {
          ...s.loading,
          fetchingProfilesById: {
            ...s.loading.fetchingProfilesById,
            ...strongFromEntries(profilesNotFetched.map((i) => [i, true])),
          },
        },
      }));

      const { data: newProfiles, error } = await supabaseApi()
        .from('profiles')
        .select('*')
        .is('deleted_at', null)
        .in('id', profilesNotFetched);

      if (isNonNil(error)) {
        console.log('what the fuckity?', profilesNotFetched);
        return;
      }

      console.log('[fetchProfilesById] fetched profiles:', newProfiles?.map((p) => p.id));

      set((s) => ({
        loading: {
          ...s.loading,
          fetchingProfilesById: {
            ...s.loading.fetchingProfilesById,
            ...strongFromEntries(profilesNotFetched.map((i) => [i, false])),
          },
        },
        profilesById: {
          ...s.profilesById,
          ...strongFromEntries((newProfiles ?? []).map((n) => [n.id, n])),
        },
      }));
    },
    searchUsers: async (query: string, cursor?: string | null) => {
      if (get().loading.searchingUsers) {
        return;
      }

      // Return cached result for this query if it's the first page
      if (!cursor && get().userSearchCache[query]) {
        return;
      }

      set((s) => ({
        loading: { ...s.loading, searchingUsers: true },
      }));

      const params = new URLSearchParams();
      if (query) {
        params.set('filter', query);
      }
      if (cursor) {
        params.set('cursor', cursor);
      }
      const paramStr = params.toString();
      const functionName = paramStr ? `account/users?${paramStr}` : 'account/users';

      const { data, error } = await supabaseApi().functions.invoke<UsersResponse>(functionName, {
        method: 'GET',
      });

      if (error || isNil(data)) {
        set((s) => ({ loading: { ...s.loading, searchingUsers: false } }));
        return;
      }

      set((s) => {
        const existing = cursor ? s.userSearchCache[query] : undefined;
        const users = existing ? [...existing.users, ...data.users] : data.users;
        return {
          loading: { ...s.loading, searchingUsers: false },
          userSearchCache: {
            ...s.userSearchCache,
            [query]: { users, nextCursor: data.nextCursor },
          },
        };
      });
    },
    registerForPushNotifications: async (deviceIdentifier: Uuid<Device>, pushToken: ExpoToken) => {
      console.log('registering for push notifications', {
        deviceIdentifier,
        pushToken,
      });
      const updating = get().loading.registerForPushNotifications;
      if (updating) {
        console.log('registering for push notifications [update already in progress]', {
          deviceIdentifier,
          pushToken,
        });
        return;
      }
      const session = get().session;
      if (isNil(session)) {
        console.log('registering for push notifications no valid session]', {
          deviceIdentifier,
          pushToken,
        });
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              registerForPushNotifications: true,
            },
          }) as const satisfies Partial<UserState>,
      );

      const userId = session.user.id as Uuid<User>;
      const isAnonymous = session.user.is_anonymous;

      console.log('checking for existing token', {
        userId,
        isAnonymous,
        pushToken,
      });

      const { data: existingToken, error } = await supabaseApi()
        .from('notification_user_tokens')
        .select('id')
        .eq('user_id', userId)
        .eq('device_identifier', deviceIdentifier)
        .eq('notification_level', isAnonymous ? 'global' : 'shows')
        .eq('token', pushToken)
        .is('deleted_at', null)
        .maybeSingle();

      console.log('result from existing check', {
        existingToken,
        error,
      });

      if (isNonNil(existingToken)) {
        console.log('registering for push notifications [found an existing token]', {
          deviceIdentifier,
          pushToken,
        });
        set(
          (s) =>
            ({
              loading: {
                ...s.loading,
                registerForPushNotifications: false,
              },
            }) as const satisfies Partial<UserState>,
        );
        return;
      }

      // delete any other tokens on this device before we create the new one
      const { data, error: deleteError } = await supabaseApi()
        .from('notification_user_tokens')
        .update({
          deleted_at: now(),
          updated_at: now(),
        })
        .eq('user_id', userId)
        .eq('device_identifier', deviceIdentifier)
        .is('deleted_at', null);

      console.log('delete result?', {
        data,
        deleteError,
      });

      console.log('registering for push notifications [deleted any duplicates]', {
        deviceIdentifier,
        pushToken,
      });

      await supabaseApi()
        .from('notification_user_tokens')
        .insert({
          user_id: userId,
          token: pushToken,
          notification_level: isAnonymous ? 'global' : 'shows',
          device_identifier: deviceIdentifier,
          token_type: 'expo',
        });

      console.log('registering for push notifications [created a new token]', {
        deviceIdentifier,
        pushToken,
      });

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              registerForPushNotifications: false,
            },
          }) as const satisfies Partial<UserState>,
      );
    },
  }) as const;
